import {QueryStatus} from '@reduxjs/toolkit/query';

import {GenericRTKQState} from '../types/GenericRTKQState';
import {isRTKState} from './isRTKState';

/**
 * Prepares an RTK Query state for rehydration
 *
 * This function:
 * 1. Filters out queries that don't have valid data
 * 2. Filters out queries of endpoints that are not in `knownEndpointNames` (renamed or removed
 *    endpoints), so stale data never reaches the store. Up to RTK 2.12, rehydrating them also
 *    crashed RTK Query's cache-collection timer, because it could not find the endpoint definition
 *    (Sentry PRICE-REPORT-6K). RTK 2.13.0 fixed that crash, so this is no longer a crash guard.
 * 3. Updates timestamps to make RTK Query treat the data as fresh
 */
export const sanitizeRtkQueryState = <S extends GenericRTKQState = GenericRTKQState>(
  state: S,
  knownEndpointNames: ReadonlyArray<string>
) => {
  if (!isRTKState(state)) {
    return state;
  }

  const now = Date.now();

  const filteredQueries = {} as GenericRTKQState['queries'];
  Object.entries(state.queries).reduce((filtered, [queryKey, query]) => {
    if (
      query?.status === QueryStatus.fulfilled &&
      query.data !== undefined &&
      knownEndpointNames.includes(query.endpointName)
    ) {
      filtered[queryKey] = {
        ...query,
        status: QueryStatus.fulfilled,
        fulfilledTimeStamp: now,
      };
    }
    return filtered;
  }, filteredQueries);

  return {
    ...state,
    queries: filteredQueries,
  };
};
